import { Chart as ChartJS, Plugin } from 'chart.js';
import { WEIGHT_PERIOD_OPTIONS, WeightWinnerPoint } from '../../types/app';

const AFTER_WEIGHT_PERIOD_LABEL =
  WEIGHT_PERIOD_OPTIONS.find((option) => option.order === 2)?.label || 'Efter Julefrokost';

export function createWeightChartAvatarPlugin(
  memberAvatarById: Map<string, HTMLImageElement>,
  weightWinnerPoints: WeightWinnerPoint[]
): Plugin<'line'> {
  type CrownHitArea = {
    x: number;
    y: number;
    radius: number;
    year: number;
    memberId: string;
  };

  type ConfettiPiece = {
    x: number;
    y: number;
    vx: number;
    vy: number;
    size: number;
    rotation: number;
    rotationSpeed: number;
    color: string;
    life: number;
  };

  const confettiColors = ['#f59e0b', '#ef4444', '#22c55e', '#0ea5e9', '#a855f7', '#eab308'];
  let crownHitAreas: CrownHitArea[] = [];
  let hoveredCrown: CrownHitArea | null = null;
  let confettiPieces: ConfettiPiece[] = [];
  let confettiFrameId: number | null = null;

  const drawRoundedRect = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ) => {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  };

  const drawWinnerTooltip = (ctx: CanvasRenderingContext2D, crown: CrownHitArea) => {
    const tooltipText = `Vinder ${crown.year}`;

    ctx.save();
    ctx.font = '12px "Manrope", sans-serif';
    const textWidth = ctx.measureText(tooltipText).width;
    const tooltipWidth = textWidth + 16;
    const tooltipHeight = 24;
    const tooltipX = crown.x - tooltipWidth / 2;
    const tooltipY = crown.y - 36;

    drawRoundedRect(ctx, tooltipX, tooltipY, tooltipWidth, tooltipHeight, 7);
    ctx.fillStyle = 'rgba(21, 39, 30, 0.92)';
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(tooltipText, tooltipX + tooltipWidth / 2, tooltipY + tooltipHeight / 2 + 0.5);
    ctx.restore();
  };

  const drawConfettiPieces = (ctx: CanvasRenderingContext2D) => {
    confettiPieces.forEach((piece) => {
      ctx.save();
      ctx.translate(piece.x, piece.y);
      ctx.rotate(piece.rotation);
      ctx.fillStyle = piece.color;
      ctx.fillRect(-piece.size / 2, -piece.size / 3, piece.size, (piece.size * 2) / 3);
      ctx.restore();
    });
  };

  const updateConfettiPieces = (chart: ChartJS<'line'>) => {
    const chartArea = chart.chartArea;
    const maxY = chartArea.bottom + 30;

    confettiPieces = confettiPieces
      .map((piece) => ({
        ...piece,
        x: piece.x + piece.vx,
        y: piece.y + piece.vy,
        vy: piece.vy + 0.08,
        rotation: piece.rotation + piece.rotationSpeed,
        life: piece.life - 1,
      }))
      .filter((piece) => piece.life > 0 && piece.y <= maxY);
  };

  const runConfettiLoop = (chart: ChartJS<'line'>) => {
    updateConfettiPieces(chart);
    chart.draw();

    if (confettiPieces.length) {
      confettiFrameId = requestAnimationFrame(() => runConfettiLoop(chart));
    } else {
      confettiFrameId = null;
    }
  };

  const spawnConfetti = (chart: ChartJS<'line'>) => {
    const chartArea = chart.chartArea;
    const burstCount = 55;

    for (let index = 0; index < burstCount; index += 1) {
      confettiPieces.push({
        x: chartArea.left + Math.random() * (chartArea.right - chartArea.left),
        y: chartArea.top - Math.random() * 70,
        vx: -1.6 + Math.random() * 3.2,
        vy: 2.2 + Math.random() * 2.8,
        size: 5 + Math.random() * 5,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: -0.14 + Math.random() * 0.28,
        color: confettiColors[index % confettiColors.length],
        life: 90 + Math.random() * 40,
      });
    }

    if (confettiFrameId === null) {
      confettiFrameId = requestAnimationFrame(() => runConfettiLoop(chart));
    }
  };

  return {
    id: 'weightChartAvatarPlugin',
    afterDatasetsDraw: (chart) => {
      const { ctx } = chart;
      crownHitAreas = [];

      const chartLabels = (chart.data.labels || []).map((label) => `${label}`);
      const winnerYearsByMember = new Map<string, number[]>();

      weightWinnerPoints.forEach((winnerPoint) => {
        const years = winnerYearsByMember.get(winnerPoint.memberId) || [];
        years.push(winnerPoint.afterYear);
        winnerYearsByMember.set(winnerPoint.memberId, years);
      });

      const findAfterLabelIndex = (year: number): number => {
        const exactLabel = `${year} - ${AFTER_WEIGHT_PERIOD_LABEL}`;
        const exactMatchIndex = chartLabels.findIndex((label) => label === exactLabel);

        if (exactMatchIndex !== -1) {
          return exactMatchIndex;
        }

        return chartLabels.findIndex(
          (label) => label.startsWith(`${year} - `) && label.toLowerCase().includes('efter')
        );
      };

      chart.data.datasets.forEach((dataset, datasetIndex) => {
        const customDataset = dataset as typeof dataset & { memberId?: string };
        const memberId = customDataset.memberId;
        if (!memberId) {
          return;
        }

        const meta = chart.getDatasetMeta(datasetIndex);
        const dataValues = Array.isArray(customDataset.data) ? customDataset.data : [];

        const winnerYears = winnerYearsByMember.get(memberId) || [];

        winnerYears.forEach((winnerYear) => {
          const winnerPointIndex = findAfterLabelIndex(winnerYear);
          if (winnerPointIndex === -1) {
            return;
          }

          const winnerValue = dataValues[winnerPointIndex];
          if (winnerValue === null || winnerValue === undefined) {
            return;
          }

          const winnerPoint = meta.data[winnerPointIndex] as { x?: number; y?: number } | undefined;
          if (!winnerPoint || typeof winnerPoint.x !== 'number' || typeof winnerPoint.y !== 'number') {
            return;
          }

          const crownX = winnerPoint.x + 13;
          const crownY = winnerPoint.y - 15;
          const crownRadius = 9;

          crownHitAreas.push({
            x: crownX,
            y: crownY,
            radius: crownRadius + 2,
            year: winnerYear,
            memberId,
          });

          ctx.save();
          ctx.beginPath();
          ctx.arc(crownX, crownY, crownRadius, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(255, 255, 255, 0.96)';
          ctx.fill();
          ctx.lineWidth = 1;
          ctx.strokeStyle = 'rgba(180, 83, 9, 0.45)';
          ctx.stroke();
          ctx.font = '14px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('👑', crownX, crownY + 0.5);
          ctx.restore();
        });

        const avatarImage = memberAvatarById.get(memberId);
        if (!avatarImage) {
          return;
        }

        if (!avatarImage.complete || avatarImage.naturalWidth === 0) {
          const imageWithFlag = avatarImage as HTMLImageElement & { __chartLoadBound?: boolean };
          if (!imageWithFlag.__chartLoadBound) {
            imageWithFlag.__chartLoadBound = true;
            avatarImage.addEventListener('load', () => chart.draw(), { once: true });
          }
          return;
        }

        const lastIndex = [...dataValues].map((value, index) => ({ value, index })).reverse().find(({ value }) => value !== null && value !== undefined)?.index;

        if (lastIndex === undefined) {
          return;
        }

        const pointElement = meta.data[lastIndex] as { x?: number; y?: number } | undefined;
        if (!pointElement || typeof pointElement.x !== 'number' || typeof pointElement.y !== 'number') {
          return;
        }

        const size = 30;
        const radius = size / 2;
        const x = pointElement.x;
        const y = pointElement.y;

        ctx.save();
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(avatarImage, x - radius, y - radius, size, size);
        ctx.restore();

        ctx.save();
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = '#ffffff';
        ctx.stroke();
        ctx.restore();
      });
    },
    afterDraw: (chart) => {
      const { ctx } = chart;
      drawConfettiPieces(ctx);

      if (hoveredCrown) {
        drawWinnerTooltip(ctx, hoveredCrown);
      }
    },
    afterEvent: (chart, args) => {
      const chartEvent = args.event;

      if (chartEvent.type === 'mouseout') {
        if (hoveredCrown || chart.canvas.style.cursor !== 'default') {
          hoveredCrown = null;
          chart.canvas.style.cursor = 'default';
          chart.draw();
        }
        return;
      }

      const eventX = typeof chartEvent.x === 'number' ? chartEvent.x : null;
      const eventY = typeof chartEvent.y === 'number' ? chartEvent.y : null;

      if (eventX === null || eventY === null) {
        return;
      }

      const hitArea = crownHitAreas.find((area) => {
        const deltaX = eventX - area.x;
        const deltaY = eventY - area.y;
        return deltaX * deltaX + deltaY * deltaY <= area.radius * area.radius;
      }) || null;

      const hoverChanged =
        (hoveredCrown?.year || null) !== (hitArea?.year || null) ||
        (hoveredCrown?.memberId || null) !== (hitArea?.memberId || null);

      hoveredCrown = hitArea;
      chart.canvas.style.cursor = hitArea ? 'pointer' : 'default';

      if (chartEvent.type === 'click' && hitArea) {
        spawnConfetti(chart);
        chart.draw();
        return;
      }

      if (hoverChanged) {
        chart.draw();
      }
    },
    afterDestroy: () => {
      if (confettiFrameId !== null) {
        cancelAnimationFrame(confettiFrameId);
        confettiFrameId = null;
      }
      hoveredCrown = null;
      crownHitAreas = [];
      confettiPieces = [];
    },
  };
}
